import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCoins } from '../hooks/useCoins';
import { useSettings } from '../hooks/useSettings';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { Badge } from '../components/ui/Badge';
import { formatPrice, formatNumber, formatPercentage, getChangeClass } from '../lib/utils';
import { TrendingUp, TrendingDown, Star, RefreshCw } from 'lucide-react';

// Force HMR update on file change
if (import.meta.hot) {
  import.meta.hot.accept();
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { coins, loading, error, refetch } = useCoins(settings.currency);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const handleCoinClick = (coinId: string) => {
    navigate(`/coin/${coinId}`);
  };

  if (loading && coins.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-black dark:text-white">Dashboard</h1>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="bg-red-100 dark:bg-red-900/30 p-4 rounded-full mb-4">
          <RefreshCw className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-xl font-semibold text-black dark:text-white mb-2">
          Unable to load data
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-md">
          {error}
        </p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-black hover:bg-gray-900 text-white dark:bg-white dark:hover:bg-gray-200 dark:text-black rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Real-time cryptocurrency prices

          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refetch}
            className={`p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors ${
              loading ? 'animate-spin' : ''
            }`}
            title="Refresh"
            disabled={loading}
          >
            <RefreshCw className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-700 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-700 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {coins.map((coin) => (
            <Card
              key={coin.id}
              onClick={() => handleCoinClick(coin.id)}
              className="animate-fade-in"
            >
              <CardHeader className="flex items-center gap-3">
                <img src={coin.image} alt={coin.name} className="h-10 w-10 rounded-full" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {coin.name}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 uppercase">
                    {coin.symbol}
                  </p>
                </div>
                <Star className="h-5 w-5 text-slate-400 hover:text-yellow-500 cursor-pointer" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {formatPrice(coin.current_price, settings.currency)}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        coin.price_change_percentage_24h > 0 ? 'success' : 'destructive'
                      }
                    >
                      {formatPercentage(coin.price_change_percentage_24h)}
                    </Badge>
                    <span className="text-xs text-gray-500 dark:text-gray-400">24h</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="block text-gray-400">Market Cap</span>
                      <span className="font-medium">{formatNumber(coin.market_cap)}</span>
                    </div>
                    <div>
                      <span className="block text-gray-400">Volume</span>
                      <span className="font-medium">{formatNumber(coin.total_volume)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Coin
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    24h %
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Market Cap
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Volume
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {coins.map((coin) => (
                  <tr
                    key={coin.id}
                    onClick={() => handleCoinClick(coin.id)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={coin.image} alt={coin.name} className="h-8 w-8 rounded-full" />
                        <div>
                          <p className="font-medium text-black dark:text-white">
                            {coin.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                            {coin.symbol}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-black dark:text-white">
                      {formatPrice(coin.current_price, settings.currency)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={getChangeClass(coin.price_change_percentage_24h)}>
                        {formatPercentage(coin.price_change_percentage_24h)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                      {formatNumber(coin.market_cap)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                      {formatNumber(coin.total_volume)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}